import { useCallback, useEffect, useRef, useState } from 'react';
import { BubbleMenu, EditorContent, useEditor, type Editor } from '@tiptap/react';
import { Extension, Node as TiptapNode, nodeInputRule, nodePasteRule } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { QUOTE_TEXT_VARIABLES } from '@catalyst-psa/shared';

const LINK_ATTRS = { rel: 'noopener noreferrer nofollow', target: '_blank' };

/**
 * Registers a `fontSize` attribute on the shared textStyle mark (rendered as an inline
 * `font-size` style). Applied from the toolbar via `setMark('textStyle', { fontSize })`.
 */
const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              const size = attributes.fontSize as string | null;
              return size ? { style: `font-size: ${size}` } : {};
            },
          },
        },
      },
    ];
  },
});

/**
 * Registers a `textAlign` attribute on paragraphs and headings (rendered as an inline
 * `text-align` style, so it survives sanitization and flows to the PDF/delivery page).
 * Applied from the toolbar; null = default (left).
 */
const TextAlign = Extension.create({
  name: 'textAlign',
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading'],
        attributes: {
          textAlign: {
            default: null,
            parseHTML: (element) => element.style.textAlign || null,
            renderHTML: (attributes) => {
              const align = attributes.textAlign as string | null;
              return align ? { style: `text-align: ${align}` } : {};
            },
          },
        },
      },
    ];
  },
});

/**
 * Image node that preserves a `data-psa-id` attribute. Inline note images are uploaded
 * to SharePoint and stored in the note HTML as `src="psa-image:{id}"`; the volatile
 * display URL is resolved on demand (see serialize/hydrate helpers below).
 */
const PsaImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      'data-psa-id': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-psa-id'),
        renderHTML: (attributes) => {
          const id = attributes['data-psa-id'] as string | null;
          return id ? { 'data-psa-id': id } : {};
        },
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute('width'),
        renderHTML: (attributes) => {
          const width = attributes.width as string | null;
          return width ? { width } : {};
        },
      },
      // left (null) / center / right — rendered as inline margin styles so the
      // alignment survives sanitization and flows to the PDF + delivery page.
      align: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute('data-align') ??
          (element.style.marginLeft === 'auto' && element.style.marginRight === 'auto'
            ? 'center'
            : element.style.marginLeft === 'auto'
              ? 'right'
              : null),
        renderHTML: (attributes) => {
          const align = attributes.align as string | null;
          if (align === 'center') {
            return {
              'data-align': 'center',
              style: 'display: block; margin-left: auto; margin-right: auto;',
            };
          }
          if (align === 'right') {
            return { 'data-align': 'right', style: 'display: block; margin-left: auto;' };
          }
          return {};
        },
      },
    };
  },
  /** Plain-DOM node view adding a drag-to-resize corner handle when editable. */
  addNodeView() {
    return ({ node, editor, getPos }) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'psa-image-resizer';
      const img = document.createElement('img');
      img.className = 'psa-note-image';

      const apply = (n: typeof node): void => {
        img.src = (n.attrs.src as string) ?? '';
        const id = n.attrs['data-psa-id'] as string | null;
        if (id) img.setAttribute('data-psa-id', id);
        else img.removeAttribute('data-psa-id');
        const width = n.attrs.width as string | null;
        if (width) img.setAttribute('width', width);
        else img.removeAttribute('width');
        if (n.attrs.alt) img.alt = n.attrs.alt as string;
        // Mirror the alignment on the editor wrapper (the saved HTML styles the <img>).
        const align = n.attrs.align as string | null;
        wrapper.style.display = align ? 'block' : '';
        wrapper.style.width = align ? 'fit-content' : '';
        wrapper.style.marginLeft = align ? 'auto' : '';
        wrapper.style.marginRight = align === 'center' ? 'auto' : '';
      };
      apply(node);
      wrapper.appendChild(img);

      let handle: HTMLSpanElement | null = null;
      if (editor.isEditable) {
        handle = document.createElement('span');
        handle.className = 'psa-image-resize-handle';
        handle.addEventListener('mousedown', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const startX = event.clientX;
          const startWidth = img.getBoundingClientRect().width;
          const widthAt = (e: MouseEvent): number =>
            Math.max(40, Math.round(startWidth + e.clientX - startX));
          const onMove = (e: MouseEvent): void => {
            img.setAttribute('width', String(widthAt(e)));
          };
          const onUp = (e: MouseEvent): void => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            const pos = typeof getPos === 'function' ? getPos() : undefined;
            if (pos == null) return;
            const current = editor.view.state.doc.nodeAt(pos);
            if (!current) return;
            editor.view.dispatch(
              editor.view.state.tr.setNodeMarkup(pos, undefined, {
                ...current.attrs,
                width: String(widthAt(e)),
              }),
            );
          };
          document.addEventListener('mousemove', onMove);
          document.addEventListener('mouseup', onUp);
        });
        wrapper.appendChild(handle);
      }

      return {
        dom: wrapper,
        update: (updated) => {
          if (updated.type.name !== node.type.name) return false;
          apply(updated);
          return true;
        },
        // Keep resize drags out of ProseMirror's drag-and-drop handling.
        stopEvent: (event) =>
          handle != null && event.target instanceof Node && handle.contains(event.target),
      };
    };
  },
}).configure({ inline: false, allowBase64: false, HTMLAttributes: { class: 'psa-note-image' } });

/** Canonical token casing for case-insensitive matches (customer.name → Customer.Name). */
const VARIABLE_TOKEN_BY_LOWER = new Map(
  QUOTE_TEXT_VARIABLES.map((v) => [v.token.toLowerCase(), v.token]),
);
const VARIABLE_TOKEN_ALTERNATION = QUOTE_TEXT_VARIABLES.map((v) =>
  v.token.replace(/\./g, '\\.'),
).join('|');

/**
 * Atomic `[Token]` PSA-data variable chip for quote/template sections. Stored as
 * `<span data-psa-var="Token">[Token]</span>` (the backend substitutes both the chip
 * span and bare `[Token]` text). Being an atom, the Link extension's autolink can
 * never underline it and break the token — typing or pasting `[Customer.Name]`
 * converts to a chip so users SEE the variable is registered while editing.
 */
const PsaVariable = TiptapNode.create<{ interactive: boolean }>({
  name: 'psaVariable',
  group: 'inline',
  inline: true,
  atom: true,
  addOptions() {
    return { interactive: false };
  },
  addAttributes() {
    return {
      token: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-psa-var'),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-psa-var]' }];
  },
  renderHTML({ node }) {
    const token = node.attrs.token as string;
    return ['span', { 'data-psa-var': token, class: 'psa-variable' }, `[${token}]`];
  },
  renderText({ node }) {
    return `[${node.attrs.token as string}]`;
  },
  addInputRules() {
    if (!this.options.interactive) return [];
    return [
      nodeInputRule({
        find: new RegExp(`\\[\\s*(${VARIABLE_TOKEN_ALTERNATION})\\s*\\]$`, 'i'),
        type: this.type,
        getAttributes: (match) => ({
          token: VARIABLE_TOKEN_BY_LOWER.get((match[1] ?? '').toLowerCase()) ?? match[1],
        }),
      }),
    ];
  },
  addPasteRules() {
    if (!this.options.interactive) return [];
    return [
      nodePasteRule({
        find: new RegExp(`\\[\\s*(${VARIABLE_TOKEN_ALTERNATION})\\s*\\]`, 'gi'),
        type: this.type,
        getAttributes: (match) => ({
          token: VARIABLE_TOKEN_BY_LOWER.get((match[1] ?? '').toLowerCase()) ?? match[1],
        }),
      }),
    ];
  },
});

/**
 * Extra style attributes shared by table cells and header cells. Both render as inline
 * styles (merged by TipTap) so they survive sanitization and reach the PDF/delivery page.
 */
const CELL_STYLE_ATTRIBUTES = {
  backgroundColor: {
    default: null as string | null,
    parseHTML: (element: HTMLElement) => element.style.backgroundColor || null,
    renderHTML: (attributes: Record<string, unknown>) => {
      const color = attributes.backgroundColor as string | null;
      return color ? { style: `background-color: ${color}` } : {};
    },
  },
  verticalAlign: {
    default: null as string | null,
    parseHTML: (element: HTMLElement) => element.style.verticalAlign || null,
    renderHTML: (attributes: Record<string, unknown>) => {
      const align = attributes.verticalAlign as string | null;
      return align ? { style: `vertical-align: ${align}` } : {};
    },
  },
};

const PsaTableCell = TableCell.extend({
  addAttributes() {
    return { ...this.parent?.(), ...CELL_STYLE_ATTRIBUTES };
  },
});

const PsaTableHeader = TableHeader.extend({
  addAttributes() {
    return { ...this.parent?.(), ...CELL_STYLE_ATTRIBUTES };
  },
});

/** Table row with a persisted `height` (rendered as an inline style — acts as a minimum). */
const PsaTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      height: {
        default: null as string | null,
        parseHTML: (element: HTMLElement) => element.style.height || null,
        renderHTML: (attributes: Record<string, unknown>) => {
          const height = attributes.height as string | null;
          return height ? { style: `height: ${height}` } : {};
        },
      },
    };
  },
});

/** Grows/shrinks the height of the row containing the cursor (8px steps, 24px floor). */
function adjustRowHeight(editor: Editor, delta: number): void {
  const { state } = editor.view;
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === 'tableRow') {
      const current = parseInt((node.attrs.height as string | null) ?? '', 10) || 32;
      const next = Math.max(24, current + delta);
      editor.view.dispatch(
        state.tr.setNodeMarkup($from.before(depth), undefined, {
          ...node.attrs,
          height: `${next}px`,
        }),
      );
      editor.view.focus();
      return;
    }
  }
}

/** Character + paragraph formatting copied by the format painter. */
interface FormatSnapshot {
  marks: { type: string; attrs: Record<string, unknown> }[];
  textAlign: string | null;
}

const PAINTER_BLOCK_TYPES = new Set(['paragraph', 'heading']);

/** Reads the formatting at the current cursor/selection start for the format painter. */
function captureFormat(editor: Editor): FormatSnapshot {
  const { state } = editor;
  const { $from, from, to, empty } = state.selection;
  const marks = empty
    ? (state.storedMarks ?? $from.marks())
    : state.doc.resolve(Math.min(from + 1, to)).marks();
  let textAlign: string | null = null;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (PAINTER_BLOCK_TYPES.has(node.type.name)) {
      textAlign = (node.attrs.textAlign as string | null) ?? null;
      break;
    }
  }
  return {
    marks: marks
      .filter((mark) => mark.type.name !== 'link')
      .map((mark) => ({ type: mark.type.name, attrs: { ...mark.attrs } })),
    textAlign,
  };
}

/**
 * Replaces the formatting of the current (non-empty) selection with a captured snapshot.
 * Links are preserved — only character formatting and paragraph alignment are painted.
 */
function applyFormat(editor: Editor, snapshot: FormatSnapshot): void {
  const { state, view } = editor;
  const { from, to, empty } = state.selection;
  if (empty) return;
  const tr = state.tr;
  for (const [name, type] of Object.entries(state.schema.marks)) {
    if (name === 'link') continue;
    tr.removeMark(from, to, type);
  }
  for (const mark of snapshot.marks) {
    const type = state.schema.marks[mark.type];
    if (type) tr.addMark(from, to, type.create(mark.attrs));
  }
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (PAINTER_BLOCK_TYPES.has(node.type.name)) {
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, textAlign: snapshot.textAlign });
    }
  });
  view.dispatch(tr);
  view.focus();
}

/** Normalizes a CSS color (browsers report inline styles as rgb()) to #rrggbb for color inputs. */
function toHexColor(value: string | null): string | null {
  if (!value) return null;
  if (value.startsWith('#')) return value;
  const match = value.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
  if (!match) return null;
  return `#${[match[1], match[2], match[3]]
    .map((n) => Number(n).toString(16).padStart(2, '0'))
    .join('')}`;
}

function buildExtensions(placeholder?: string, withVariables = false) {
  return [
    StarterKit.configure({ heading: { levels: [2, 3] } }),
    Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: LINK_ATTRS }),
    PsaImage,
    PsaVariable.configure({ interactive: withVariables }),
    TextStyle,
    Color,
    FontFamily,
    FontSize,
    TextAlign,
    Table.configure({ resizable: true }),
    PsaTableRow,
    PsaTableHeader,
    PsaTableCell,
    ...(placeholder ? [Placeholder.configure({ placeholder })] : []),
  ];
}

const UUID_RE = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

/**
 * Rewrites freshly-uploaded inline images so the saved note HTML stores the stable
 * `psa-image:{id}` reference instead of the short-lived display URL. Call before saving.
 */
export function serializeNoteImages(html: string): string {
  if (typeof window === 'undefined' || !html.includes('data-psa-id')) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('img[data-psa-id]').forEach((img) => {
    const id = img.getAttribute('data-psa-id');
    if (id) {
      img.setAttribute('src', `psa-image:${id}`);
      img.removeAttribute('data-psa-id');
    }
  });
  return doc.body.innerHTML;
}

/** Extracts the inline-image ids referenced by a stored note body. */
export function collectNoteImageIds(html: string): string[] {
  const ids: string[] = [];
  const regex = new RegExp(`psa-image:(${UUID_RE})`, 'g');
  let match: RegExpExecArray | null;
  while ((match = regex.exec(html)) !== null) {
    if (match[1]) ids.push(match[1]);
  }
  return ids;
}

/**
 * Replaces `psa-image:{id}` references with fresh display URLs before rendering.
 *
 * The stable id is re-attached as `data-psa-id` on every `<img>` it rewrites: the
 * display URL is a short-lived (~1h) SharePoint pre-signed link, so without the id a
 * later save would serialize that expiring URL into the stored HTML and the picture
 * would break once the link expired (see serializeNoteImages).
 */
export function hydrateNoteImages(html: string, urls: Record<string, string>): string {
  const imgSrcRe = new RegExp(`src\\s*=\\s*["']?psa-image:(${UUID_RE})`, 'i');
  const out = html.replace(/<img\b[^>]*>/gi, (tag) => {
    const id = imgSrcRe.exec(tag)?.[1];
    if (!id) return tag;
    const url = urls[id];
    if (!url) return tag;
    // Function replacer: display URLs may contain `$`, which is special in string ones.
    const withUrl = tag.replace(`psa-image:${id}`, () => url);
    return withUrl.includes('data-psa-id')
      ? withUrl
      : withUrl.replace(/<img\b/i, `<img data-psa-id="${id}"`);
  });
  // Any remaining refs outside an <img> tag (defensive).
  return out.replace(new RegExp(`psa-image:(${UUID_RE})`, 'g'), (full, id: string) => {
    const url = urls[id];
    return url ?? full;
  });
}

/**
 * Collects File objects from a clipboard, checking both `files` and `items` (some
 * browsers expose a pasted screenshot only via `items`).
 */
function filesFromClipboard(data: DataTransfer | null): File[] {
  if (!data) return [];
  const files = Array.from(data.files);
  if (files.length > 0) return files;
  const fromItems: File[] = [];
  for (const item of Array.from(data.items)) {
    if (item.kind === 'file') {
      const file = item.getAsFile();
      if (file) fromItems.push(file);
    }
  }
  return fromItems;
}

/** Uploads any image file found in a paste/drop event into the editor. */
async function insertUploadedImage(
  editor: Editor,
  file: File,
  upload: (file: File) => Promise<{ id: string; url: string }>,
): Promise<void> {
  const { id, url } = await upload(file);
  // Insert the image node with both the fresh display URL and the stable id in one step
  // (a separate updateAttributes after setImage would not target the new node, so the
  // id — needed to persist the image past the URL's ~1h lifetime — would be lost).
  editor
    .chain()
    .focus()
    .insertContent({ type: 'image', attrs: { src: url, 'data-psa-id': id } })
    .run();
}

interface ToolbarButtonProps {
  active?: boolean;
  onClick: () => void;
  onDoubleClick?: () => void;
  disabled?: boolean;
  label: string;
  title: string;
}

function ToolbarButton({
  active,
  onClick,
  onDoubleClick,
  disabled,
  label,
  title,
}: ToolbarButtonProps): JSX.Element {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      className={`rounded px-2 py-1 text-xs font-medium ${
        disabled
          ? 'cursor-not-allowed text-slate-300'
          : active
            ? 'bg-brand-100 text-brand-700'
            : 'text-slate-600 hover:bg-slate-100'
      }`}
    >
      {label}
    </button>
  );
}

interface ToolbarProps {
  editor: Editor;
  withVariables?: boolean;
  /** Format painter state, owned by the editor so the sticky + floating bars stay in sync. */
  painterActive?: boolean;
  painterLocked?: boolean;
  onPainterClick?: () => void;
  onPainterDoubleClick?: () => void;
}

function Toolbar({
  editor,
  withVariables,
  painterActive,
  painterLocked,
  onPainterClick,
  onPainterDoubleClick,
}: ToolbarProps): JSX.Element {
  const setLink = (): void => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', previous ?? '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const fontFamily = (editor.getAttributes('textStyle').fontFamily as string | null) ?? '';
  const fontSize = (editor.getAttributes('textStyle').fontSize as string | null) ?? '';
  const color = (editor.getAttributes('textStyle').color as string | null) ?? '#0f172a';
  const inTable = editor.isActive('table');
  const setAlign = (align: 'center' | 'right' | null): void => {
    editor
      .chain()
      .focus()
      .updateAttributes('paragraph', { textAlign: align })
      .updateAttributes('heading', { textAlign: align })
      // A selected image aligns itself (it is a block node, so text-align can't move it).
      .updateAttributes('image', { align })
      .run();
  };
  const alignActive = (align: string): boolean =>
    editor.isActive({ textAlign: align }) || editor.isActive('image', { align });
  const setVerticalAlign = (align: 'top' | 'middle' | 'bottom'): void => {
    editor.chain().focus().setCellAttribute('verticalAlign', align).run();
  };
  const verticalAlign =
    (editor.getAttributes('tableCell').verticalAlign as string | null) ??
    (editor.getAttributes('tableHeader').verticalAlign as string | null) ??
    null;
  const cellBackground =
    toHexColor(
      (editor.getAttributes('tableCell').backgroundColor as string | null) ??
        (editor.getAttributes('tableHeader').backgroundColor as string | null),
    ) ?? '#ffffff';

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 px-2 py-1">
      {onPainterClick ? (
        <>
          <ToolbarButton
            title={
              painterActive
                ? `Format painter armed${painterLocked ? ' (locked — click again to stop)' : ''} — select text to apply`
                : 'Format painter — click to copy formatting from the cursor, double-click to lock it on'
            }
            label={painterLocked ? '🖌🔒' : '🖌'}
            active={painterActive}
            onClick={onPainterClick}
            onDoubleClick={onPainterDoubleClick}
          />
          <span className="mx-1 h-4 w-px bg-slate-200" aria-hidden />
        </>
      ) : null}
      <ToolbarButton
        title="Bold"
        label="B"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        title="Italic"
        label="I"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        title="Bullet list"
        label="• List"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        title="Numbered list"
        label="1. List"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <span className="mx-1 h-4 w-px bg-slate-200" aria-hidden />

      <ToolbarButton
        title="Align left"
        label="⟸"
        active={!alignActive('center') && !alignActive('right')}
        onClick={() => setAlign(null)}
      />
      <ToolbarButton
        title="Align center"
        label="⟺"
        active={alignActive('center')}
        onClick={() => setAlign('center')}
      />
      <ToolbarButton
        title="Align right"
        label="⟹"
        active={alignActive('right')}
        onClick={() => setAlign('right')}
      />
      <ToolbarButton
        title={
          inTable
            ? 'Align cell contents to the top'
            : 'Align top — available inside a table cell'
        }
        label="⇑"
        disabled={!inTable}
        active={verticalAlign === 'top'}
        onClick={() => setVerticalAlign('top')}
      />
      <ToolbarButton
        title={
          inTable
            ? 'Align cell contents to the middle'
            : 'Align middle — available inside a table cell'
        }
        label="⇕"
        disabled={!inTable}
        active={verticalAlign === 'middle'}
        onClick={() => setVerticalAlign('middle')}
      />
      <ToolbarButton
        title={
          inTable
            ? 'Align cell contents to the bottom'
            : 'Align bottom — available inside a table cell'
        }
        label="⇓"
        disabled={!inTable}
        active={verticalAlign === 'bottom'}
        onClick={() => setVerticalAlign('bottom')}
      />
      <ToolbarButton
        title="Link"
        label="Link"
        active={editor.isActive('link')}
        onClick={setLink}
      />
      {withVariables ? (
        <select
          title="Insert a PSA data variable — replaced with live data on the PDF and delivery page"
          value=""
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            const token = e.target.value;
            if (!token) return;
            editor
              .chain()
              .focus()
              .insertContent([
                { type: 'psaVariable', attrs: { token } },
                { type: 'text', text: ' ' },
              ])
              .run();
          }}
          className="h-6 max-w-28 rounded border-0 bg-transparent px-1 text-xs text-slate-600 hover:bg-slate-100 focus:outline-none"
        >
          <option value="">Variable</option>
          {QUOTE_TEXT_VARIABLES.map((v) => (
            <option key={v.token} value={v.token}>
              {v.label}
            </option>
          ))}
        </select>
      ) : null}

      <span className="mx-1 h-4 w-px bg-slate-200" aria-hidden />

      <select
        title="Font"
        value={fontFamily}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          const value = e.target.value;
          if (value) editor.chain().focus().setFontFamily(value).run();
          else editor.chain().focus().unsetFontFamily().run();
        }}
        className="h-6 max-w-28 rounded border-0 bg-transparent px-1 text-xs text-slate-600 hover:bg-slate-100 focus:outline-none"
      >
        <option value="">Font</option>
        <option value="Arial, sans-serif">Arial</option>
        <option value="Georgia, serif">Georgia</option>
        <option value="'Times New Roman', serif">Times New Roman</option>
        <option value="'Courier New', monospace">Courier New</option>
        <option value="Verdana, sans-serif">Verdana</option>
      </select>
      <select
        title="Text size"
        value={fontSize}
        onChange={(e) => {
          const value = e.target.value;
          const chain = editor.chain().focus();
          if (value) chain.setMark('textStyle', { fontSize: value }).run();
          else chain.setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
        }}
        className="h-6 rounded border-0 bg-transparent px-1 text-xs text-slate-600 hover:bg-slate-100 focus:outline-none"
      >
        <option value="">Size</option>
        <option value="10px">10</option>
        <option value="12px">12</option>
        <option value="14px">14</option>
        <option value="16px">16</option>
        <option value="18px">18</option>
        <option value="24px">24</option>
        <option value="32px">32</option>
      </select>
      <label
        title="Text color"
        className="flex h-6 cursor-pointer items-center gap-1 rounded px-1 text-xs text-slate-600 hover:bg-slate-100"
      >
        <span className="font-medium" style={{ color }}>
          A
        </span>
        <input
          type="color"
          value={color}
          onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          className="h-4 w-5 cursor-pointer border-0 bg-transparent p-0"
        />
      </label>
      <ToolbarButton
        title="Clear color"
        label="A×"
        onClick={() => editor.chain().focus().unsetColor().run()}
      />

      <span className="mx-1 h-4 w-px bg-slate-200" aria-hidden />

      {inTable ? (
        <>
          <ToolbarButton
            title="Add row below"
            label="+Row"
            onClick={() => editor.chain().focus().addRowAfter().run()}
          />
          <ToolbarButton
            title="Add column after"
            label="+Col"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
          />
          <ToolbarButton
            title="Delete row"
            label="−Row"
            onClick={() => editor.chain().focus().deleteRow().run()}
          />
          <ToolbarButton
            title="Delete column"
            label="−Col"
            onClick={() => editor.chain().focus().deleteColumn().run()}
          />
          <ToolbarButton
            title="Toggle header row"
            label="Hdr"
            onClick={() => editor.chain().focus().toggleHeaderRow().run()}
          />
          <ToolbarButton
            title="Make this row taller"
            label="Row+"
            onClick={() => adjustRowHeight(editor, 8)}
          />
          <ToolbarButton
            title="Make this row shorter"
            label="Row−"
            onClick={() => adjustRowHeight(editor, -8)}
          />
          <label
            title="Cell background color (applies to all selected cells)"
            className="flex h-6 cursor-pointer items-center gap-1 rounded px-1 text-xs text-slate-600 hover:bg-slate-100"
          >
            Fill
            <input
              type="color"
              value={cellBackground}
              onChange={(e) =>
                editor.chain().focus().setCellAttribute('backgroundColor', e.target.value).run()
              }
              className="h-4 w-5 cursor-pointer border-0 bg-transparent p-0"
            />
          </label>
          <ToolbarButton
            title="Clear cell background"
            label="Fill×"
            onClick={() => editor.chain().focus().setCellAttribute('backgroundColor', null).run()}
          />
          <ToolbarButton
            title="Delete table"
            label="✕ Table"
            onClick={() => editor.chain().focus().deleteTable().run()}
          />
        </>
      ) : (
        <ToolbarButton
          title="Insert table"
          label="Table"
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
        />
      )}
    </div>
  );
}

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /**
   * When provided, pasted/dropped images are uploaded via this callback and inserted
   * inline. Returns the stored attachment id + a fresh display URL.
   */
  onUploadImage?: (file: File) => Promise<{ id: string; url: string }>;
  /** Enables the `[Token]` PSA-data variable chips + toolbar menu (quote sections). */
  withVariables?: boolean;
}

/** Controlled TipTap WYSIWYG editor (bold/italic/lists/links, optional image paste). */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  onUploadImage,
  withVariables,
}: RichTextEditorProps): JSX.Element {
  // Keep the latest upload handler in a ref so the editor's paste/drop hooks (created
  // once) always call the current callback.
  const uploadRef = useRef(onUploadImage);
  useEffect(() => {
    uploadRef.current = onUploadImage;
  }, [onUploadImage]);

  // Stable ref to the editor for use inside the paste/drop hooks below (declared before
  // useEditor so the hook closures can reference it).
  const editorRef = useRef<Editor | null>(null);

  const handleFiles = (files: File[]): boolean => {
    const upload = uploadRef.current;
    const editor = editorRef.current;
    if (!upload || !editor) return false;
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) return false;
    for (const file of images) {
      void insertUploadedImage(editor, file, upload).catch(() => {
        // Swallow — a failed paste simply inserts nothing; the user can retry.
      });
    }
    return true;
  };

  const editor = useEditor({
    extensions: buildExtensions(placeholder, withVariables),
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: { class: 'tiptap-content px-3 py-2 text-sm text-slate-900' },
      handlePaste: (_view, event) => handleFiles(filesFromClipboard(event.clipboardData)),
      handleDrop: (_view, event) => {
        const dt = event instanceof DragEvent ? event.dataTransfer : null;
        return handleFiles(dt ? Array.from(dt.files) : []);
      },
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    if (editor && !editor.isDestroyed && value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }
  }, [editor, value]);

  // --- Format painter -------------------------------------------------------
  const [painter, setPainter] = useState<FormatSnapshot | null>(null);
  const [painterLocked, setPainterLocked] = useState(false);
  const painterRef = useRef<FormatSnapshot | null>(null);
  const painterLockedRef = useRef(false);
  painterRef.current = painter;
  painterLockedRef.current = painterLocked;

  const disarmPainter = useCallback((): void => {
    setPainter(null);
    setPainterLocked(false);
  }, []);

  const armPainter = useCallback(
    (locked: boolean): void => {
      const current = editorRef.current;
      if (!current || current.isDestroyed) return;
      setPainter(captureFormat(current));
      setPainterLocked(locked);
    },
    [],
  );

  const handlePainterClick = useCallback((): void => {
    if (painterRef.current) disarmPainter();
    else armPainter(false);
  }, [armPainter, disarmPainter]);

  const handlePainterDoubleClick = useCallback((): void => {
    armPainter(true);
  }, [armPainter]);

  // Painting happens when the user finishes selecting text with the mouse. A one-shot
  // painter disarms after the first application; a locked painter keeps going.
  useEffect(() => {
    if (!editor) return undefined;
    const dom = editor.view.dom;
    const onMouseUp = (): void => {
      if (!painterRef.current) return;
      window.setTimeout(() => {
        const snapshot = painterRef.current;
        const current = editorRef.current;
        if (!snapshot || !current || current.isDestroyed) return;
        if (current.state.selection.empty) return;
        applyFormat(current, snapshot);
        if (!painterLockedRef.current) disarmPainter();
      }, 0);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && painterRef.current) disarmPainter();
    };
    dom.addEventListener('mouseup', onMouseUp);
    dom.addEventListener('keydown', onKeyDown);
    return () => {
      dom.removeEventListener('mouseup', onMouseUp);
      dom.removeEventListener('keydown', onKeyDown);
    };
  }, [editor, disarmPainter]);

  return (
    <div
      className={`tiptap-editor rounded-lg border border-slate-300 focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500${
        painter ? ' [&_.ProseMirror]:cursor-copy' : ''
      }`}
    >
      {editor ? (
        <Toolbar
          editor={editor}
          withVariables={withVariables}
          painterActive={painter !== null}
          painterLocked={painterLocked}
          onPainterClick={handlePainterClick}
          onPainterDoubleClick={handlePainterDoubleClick}
        />
      ) : null}
      {editor ? (
        <BubbleMenu
          editor={editor}
          tippyOptions={{ placement: 'top', maxWidth: 'none', duration: 100 }}
          shouldShow={({ editor: active, from, to }) => active.isEditable && from !== to}
        >
          <div className="max-w-[36rem] rounded-lg border border-slate-300 bg-white shadow-lg">
            <Toolbar
              editor={editor}
              withVariables={withVariables}
              painterActive={painter !== null}
              painterLocked={painterLocked}
              onPainterClick={handlePainterClick}
              onPainterDoubleClick={handlePainterDoubleClick}
            />
          </div>
        </BubbleMenu>
      ) : null}
      <EditorContent editor={editor} />
    </div>
  );
}

/** Read-only TipTap renderer. Parsing through the schema strips unsafe markup. */
export function RichTextContent({ html }: { html: string }): JSX.Element {
  const editor = useEditor({
    extensions: buildExtensions(),
    content: html,
    editable: false,
    editorProps: { attributes: { class: 'tiptap-content text-sm text-slate-700' } },
  });

  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      editor.commands.setContent(html, false);
    }
  }, [editor, html]);

  return <EditorContent editor={editor} />;
}
